import { useState, useEffect } from "react";
import { useAuth } from "@/auth-provider";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, X, Search, GripVertical, Star } from "lucide-react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Button } from "@/components/ui/button";
import { useGeocode } from "@/lib/use-geocode";

interface SavedLocation {
  id: string;
  label: string;
  lat: number;
  lon: number;
  country: string;
  isDefault: boolean;
}

export function SavedLocations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<SavedLocation[]>([]);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const { results: geoResults } = useGeocode(search, { enabled: showSearch });
  const results = geoResults.slice(0, 5);

  useEffect(() => {
    if (!user?.id) return;
    fetch("/api/locations")
      .then((r) => r.json())
      .then((d) => {
        if (d.locations) setLocations(d.locations);
      })
      .catch(() => {});
  }, [user?.id]);

  const handleSearch = (q: string) => {
    setSearch(q);
  };

  const handleAdd = async (loc: any) => {
    if (!user?.id) return;
    try {
      const res = await fetch("/api/locations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: loc.name, lat: loc.lat, lon: loc.lon, country: loc.country }),
      });
      if (res.ok) {
        const data = await res.json();
        setLocations((prev) => [...prev, data.location]);
      }
    } catch {}
    setSearch("");
    setShowSearch(false);
  };

  const handleRemove = async (id: string) => {
    if (!user?.id) return;
    try {
      await fetch(`/api/locations?id=${id}`, { method: "DELETE" });
      setLocations((prev) => prev.filter((l) => l.id !== id));
    } catch {}
  };

  if (!user?.id) {
    return (
      <GlassPanel intensity="low" className="p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <MapPin className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Saved Locations</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Sign in to save and manage your favorite locations across devices.
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
          Sign In to Enable
        </Button>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel intensity="low" className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <MapPin className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Saved Locations</h3>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowSearch(!showSearch)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>

      {showSearch && (
        <div className="mb-3 relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground/50" />
          <input
            type="text"
            placeholder="Search city..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-border/40 bg-background/50 backdrop-blur-sm pl-8 pr-3 py-2 text-sm outline-none focus:border-primary/50 transition-colors"
          />
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl max-h-48 overflow-y-auto">
              {results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => handleAdd(r)}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground/50" />
                  <span>{r.name}, {r.country}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {locations.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No saved locations yet. Add one above.
        </p>
      ) : (
        <div className="space-y-2">
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center gap-3 rounded-lg border border-border/30 bg-background/30 px-3 py-2 group"
            >
              {loc.isDefault && <Star className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
              {!loc.isDefault && <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{loc.label}</p>
                <p className="text-[10px] text-muted-foreground">
                  {loc.lat.toFixed(2)}, {loc.lon.toFixed(2)} • {loc.country}
                </p>
              </div>
              <button
                onClick={() => handleRemove(loc.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}